/**
 * User search hook — debounced search with TanStack Query.
 * 300ms debounce to avoid excessive API calls.
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { searchUsers } from '@turnifly/shared/api'
import type { SearchUser } from '@turnifly/shared/api'
import { feedKeys } from './use-feed'

export function useUserSearch(currentUserId: string | undefined) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  const result = useQuery<SearchUser[]>({
    queryKey: feedKeys.search(debouncedQuery),
    queryFn: () => searchUsers(debouncedQuery, currentUserId!),
    enabled: !!currentUserId && debouncedQuery.length >= 2,
    staleTime: 30_000,
  })

  return {
    query,
    setQuery,
    results: result.data ?? [],
    isSearching: result.isLoading && debouncedQuery.length >= 2,
    isActive: query.length > 0,
    clear: () => {
      setQuery('')
      setDebouncedQuery('')
    },
  }
}
